import { type ReactElement, useState, useRef, useEffect } from "react";
import { FormattedMessage } from "react-intl";

import Button from "common/core/button";
import Icon from "common/core/icon";
import SROnly from "common/core/screen_reader";

import Styles from "./mobile_pdf_toolbar.module.scss";

type ChildrenRenderProp = {
  handleToolClick: () => void;
};

type Props = {
  signerIds: string[];
  document: {
    id: string;
    annotations: {
      edges: {
        node: {
          id: string;
          authorId: string;
        };
      }[];
    };
  };
  onDone: () => void;
  children: (props: ChildrenRenderProp) => ReactElement[];
};

const instructionsStorageKey = "hasSeenMobilePdfToolbarInstructions";

function MobilePdfToolbar({ document, signerIds, onDone, children }: Props) {
  const [instructions, setInstructions] = useState(() => ({
    hasSeen: window.sessionStorage.getItem(instructionsStorageKey) === "true",
    visible: false,
  }));

  const initialDocumentAnnotations = useRef<{ documentId: string; annotationIds?: Set<string> }>({
    documentId: "",
  });

  const dismissInstructions = () => {
    setInstructions({ hasSeen: true, visible: false });
  };

  const toolClick = () => {
    if (!instructions.hasSeen) {
      window.sessionStorage.setItem(instructionsStorageKey, "true");
      setInstructions({
        hasSeen: true,
        visible: true,
      });
    }
  };

  useEffect(() => {
    const currentAnnotations = document.annotations.edges.map((e) => e.node);
    const { current: initial } = initialDocumentAnnotations;

    if (initial.documentId !== document.id) {
      initial.documentId = document.id;
      initial.annotationIds = new Set(currentAnnotations.map((a) => a.id));
    }

    if (instructions.hasSeen && instructions.visible) {
      const newSignerPlacedAnnotation = currentAnnotations.find(
        ({ id, authorId }) =>
          initial.annotationIds && !initial.annotationIds.has(id) && signerIds.includes(authorId),
      );
      if (newSignerPlacedAnnotation) {
        dismissInstructions();
      }
    }
  }, [document.id, document.annotations]);

  return (
    <>
      <div
        className={Styles.placeToolInstructionsContainer}
        data-automation-id="mobile-pdf-annotations-toolbar"
      >
        {instructions.visible && (
          <span className={Styles.placeToolInstructions}>
            <Icon name="touch" />
            <FormattedMessage
              id="5dfe2dff-f9de-4bda-8e5a-672242ffd086"
              defaultMessage="Tap anywhere to place"
            />
            <button type="button" className={Styles.closeButton} onClick={dismissInstructions}>
              <SROnly>
                <FormattedMessage
                  id="5dfe2dff-f9de-4bda-8e5a-672242ffd086"
                  defaultMessage="Dismiss"
                />
              </SROnly>
              <Icon name="x" />
            </button>
          </span>
        )}
      </div>
      <div className={Styles.mobilePdfToolbar}>
        <Button
          buttonColor="action"
          variant="primary"
          className={Styles.doneButton}
          onClick={onDone}
        >
          <FormattedMessage id="b8444d30-c3b8-43b8-b173-34ac2633532d" defaultMessage="Done" />
        </Button>
        <ul className={Styles.toolbarScrollContainer}>
          {children({ handleToolClick: toolClick })}
        </ul>
      </div>
    </>
  );
}

export default MobilePdfToolbar;
